/**
 * FAT-327 — delivery times on the cart line and on the order.
 *
 * Two jobs, both fed by Liquid so the wording always matches what the customer saw:
 *
 *  1. `_delivery_time` line item property. et-delivery-line-property.liquid renders the hidden
 *     input server-side; for forms with a variant picker it also renders a variant id -> message
 *     map, and this file re-points the input at the variant that is actually being submitted. The
 *     listener runs on `document` in the capture phase, so it fires before the form's own submit
 *     handler serialises the FormData.
 *
 *  2. Order note attributes. The cart forms already carry the attributes as hidden inputs, which
 *     covers the checkout submit. This file additionally pushes them to cart/update.js as soon as
 *     the cart changes, so anything reading the cart before checkout (Klaviyo, apps) sees them.
 *
 * The configurator reads the map through `window.ETDeliveryTimes` — see post-selection.js.
 */
(function () {
  'use strict';

  var PROPERTY_NAME = 'properties[_delivery_time]';

  /* ------------------------------------------------------------------ variant id -> message */

  var deliveryTimes = {};

  function readMap(el) {
    try {
      return JSON.parse(el.textContent) || {};
    } catch (e) {
      return {};
    }
  }

  function collectDeliveryTimes() {
    document.querySelectorAll('[data-et-delivery-times]').forEach(function (el) {
      var map = readMap(el);
      Object.keys(map).forEach(function (variantId) {
        deliveryTimes[variantId] = map[variantId];
      });
    });
  }

  collectDeliveryTimes();

  window.ETDeliveryTimes = {
    // Empty string for a variant with no delivery metafield, and for one we know nothing about:
    // both mean "add no property".
    get: function (variantId) {
      return deliveryTimes[String(variantId)] || '';
    },
    // Quick-buy injects its markup after load, so its map arrives late.
    refresh: collectDeliveryTimes,
  };

  /* ------------------------------------------------------- line item property on form submit */

  // The variant that will actually be submitted. The form's id field lags while the variant
  // picker's section re-render is in flight, and the ET pricing block corrects it from the
  // checked option radios in its own submit listener (product-info.liquid, FAT-218) — which runs
  // AFTER this capture listener. Resolve from the radios the same way it does, so the property
  // and the corrected id can never disagree; fall back to the form's own id field.
  function submittedVariantId(form) {
    var radios = Array.prototype.filter.call(form.elements, function (el) {
      return el.matches && el.matches('input[data-option-position]:checked');
    });

    if (radios.length > 0) {
      var formId = form.getAttribute('id');
      var variantsEl = formId
        ? document.querySelector(
            '.et-pricing-display[data-et-form-id="' + formId + '"] script[data-et-variants]'
          )
        : null;

      if (variantsEl) {
        try {
          var match = (JSON.parse(variantsEl.textContent) || []).find(function (variant) {
            return radios.every(function (input) {
              var position = input.getAttribute('data-option-position');
              return variant['option' + position] === input.value;
            });
          });
          if (match) {
            return String(match.id);
          }
        } catch (e) {
          // Malformed payload — the form's id field below is still the best answer.
        }
      }
    }

    return new FormData(form).get('id');
  }

  // Returns null when this page knows nothing about the variant — the value rendered into the
  // input server-side is then the best we have and is left alone (cart drawer upsells).
  function messageFor(form, variantId) {
    var key = String(variantId);
    var scoped = form.querySelector('[data-et-delivery-times]');

    if (scoped) {
      var map = readMap(scoped);
      if (Object.prototype.hasOwnProperty.call(map, key)) {
        return map[key];
      }
    }

    return Object.prototype.hasOwnProperty.call(deliveryTimes, key) ? deliveryTimes[key] : null;
  }

  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      if (!form || !form.elements) {
        return;
      }

      var inputs = Array.prototype.filter.call(form.elements, function (el) {
        return el.name === PROPERTY_NAME && el.hasAttribute('data-et-delivery-time-sync');
      });
      if (inputs.length === 0) {
        return;
      }

      var variantId = submittedVariantId(form);
      if (!variantId) {
        return;
      }

      var message = messageFor(form, variantId);
      if (message === null) {
        return;
      }

      inputs.forEach(function (input) {
        input.value = message;
      });
    },
    true
  );

  /* ---------------------------------------------------------------- order note attributes */

  var lastSynced = null;

  function syncCartAttributes() {
    var el = document.querySelector('[data-et-delivery-attributes]');
    if (!el || el.getAttribute('data-et-delivery-attributes-dirty') !== 'true') {
      return;
    }

    var attributes = readMap(el);
    var fingerprint = JSON.stringify(attributes);
    if (Object.keys(attributes).length === 0 || fingerprint === lastSynced) {
      return;
    }

    lastSynced = fingerprint;

    fetch(window.Shopify.routes.root + 'cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ attributes: attributes }),
    }).catch(function () {
      // Let the next cart change retry; the hidden inputs in the cart form still carry the
      // attributes into the order either way.
      lastSynced = null;
    });
  }

  // The cart sections re-render asynchronously after a cart change and the theme gives no "done"
  // event, so re-read the payload a few times instead of guessing one delay. Every call after the
  // first is a no-op unless the markup actually changed, and a plain page load syncs anyway.
  function scheduleSync() {
    [200, 800, 2000].forEach(function (delay) {
      setTimeout(syncCartAttributes, delay);
    });
  }

  syncCartAttributes();

  ['cart:change', 'cart:refresh', 'variant:add'].forEach(function (eventName) {
    document.addEventListener(eventName, scheduleSync);
  });
})();

// FAT-327 re-sync 2026-08-12: the live back-to-school theme was created from the branch hours
// before FAT-327 merged, and the GitHub sync never applied these files to it. This commit touches
// every FAT-327 file so the merge re-applies them to the theme.
